import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import clsx from 'clsx'
import { DropzoneArea, DropzoneAreaProps } from 'material-ui-dropzone'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { styles } from './dropzoneDialog.styles'

export type DropzoneDialogProps = {
  open: boolean
  title: string
  /** Content rendered above the dropzone (a form, inputs, etc.) */
  children?: React.ReactNode
  /** Props forwarded directly to DropzoneArea — all DropzoneArea options are available */
  dropzoneProps?: Omit<DropzoneAreaProps, 'onChange'>
  /** Called when the user confirms. Receives the currently selected files. */
  onSave: (files: File[]) => void
  /** Called when the user cancels / goes back */
  onBack: () => void
  /** When true the save button is disabled (in addition to the internal "no files" check) */
  saveDisabled?: boolean
  /** When false, save is allowed without selecting a file. Defaults to true (current behavior) */
  requireFile?: boolean
  /** When true, the dropzone area itself is not rendered (only children). Defaults to false */
  hideDropzone?: boolean
  /** When true, removes the extra bottom spacing and the Form's toolbar min-height around children. Defaults to false (current behavior) */
  compactChildren?: boolean
  backLabel?: string
  saveLabel?: string
}

export const DropzoneDialog: React.FC<DropzoneDialogProps> = ({
  open,
  title,
  children,
  dropzoneProps,
  onSave,
  onBack,
  saveDisabled = false,
  requireFile = true,
  hideDropzone = false,
  compactChildren = false,
  backLabel,
  saveLabel
}) => {
  const [t] = useTranslation(['globalerisLibrary'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [files, setFiles] = useState<File[]>([])

  const isSaveDisabled = saveDisabled || (requireFile && files.length === 0)

  const handleSave = () => {
    if (!requireFile || files.length > 0) {
      onSave(files)
    }
  }

  return (
    <Dialog
      fullWidth
      disableBackdropClick
      disableEscapeKeyDown
      onClose={() => {}}
      open={open}
      className={classes.root}
    >
      <MuiDialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h5" color="secondary">
          {title}
        </Typography>
      </MuiDialogTitle>

      <DialogContent>
        {children && (
          <Box
            className={clsx(classes.childrenWrapper, {
              [classes.childrenWrapperCompact]: compactChildren
            })}
          >
            {children}
          </Box>
        )}

        {!hideDropzone && (
          <Box marginX={2}>
            <DropzoneArea
              dropzoneClass={classes.dropZone}
              previewGridProps={{ container: { spacing: 1, direction: 'row' } }}
              previewChipProps={{ classes: { root: classes.previewChip } }}
              showAlerts={false}
              showPreviews={true}
              showPreviewsInDropzone={false}
              useChipsForPreview
              clearOnUnmount={true}
              filesLimit={1}
              dropzoneText={
                dropzoneProps?.dropzoneText ?? t('globalerisLibrary:dropzoneDialog.dropzoneText')
              }
              {...dropzoneProps}
              onChange={setFiles}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActionsRoot}>
        <Button size="small" onClick={onBack}>
          {backLabel ?? t('globalerisLibrary:dropzoneDialog.buttons.cancel')}
        </Button>
        <Button size="small" onClick={handleSave} color="primary" disabled={isSaveDisabled}>
          {saveLabel ?? t('globalerisLibrary:dropzoneDialog.buttons.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
